import React, { PropsWithChildren, ReactNode, useRef, useState } from 'react';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import { Box, Button, CircularProgress, Link, Stack } from '@mui/material';
import ChooseProjectDialogTab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';

import { usePromise } from '@paragon/client-tools';
import { designService } from '@paragon/data-connections';
import {
  canStartTrial,
  getClientUrls,
  hasFreeSubscription,
  isLoggedInUserRoot,
  isSelfServiceUser,
} from '@paragon/login-tools';
import { OptionalTooltip } from '@paragon/material';
import { InlineNotification, InlineNotificationType } from '@paragon/notification-tools';
import { closeCurrentPopup, Dialog } from '@paragon/popup-state';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import { useAppDispatch, useAppSelector } from '../../hooks';
import { CreateProjectPanel, CreateProjectPanelMethods } from './CreateProjectPanel';
import { OpenSamplePanel } from './OpenSamplePanel';
import { PickProjectPanel } from './PickProjectPanel';

type ChooseProjectDialogTab = 'pick-project' | 'create-project' | 'open-sample';

/** Why the dialog was opened, when it was opened in response to something going wrong. */
type ChooseProjectDialogReason = 'project-load-failed';

const REASON_MESSAGES: Record<ChooseProjectDialogReason, string> = {
  'project-load-failed':
    'That project could not be opened. If it does not belong to your organization, request that they create a Shared Component Set.',
};

const REASON_MESSAGE_TYPES: Record<ChooseProjectDialogReason, InlineNotificationType> = {
  'project-load-failed': 'error',
};

const TRAINING_URL = 'https://docs.paragontruss.com/training';
const DOCUMENTATION_URL = 'https://docs.paragontruss.com/design';

export type ChooseProjectDialogProps = {
  readonly initialTab?: ChooseProjectDialogTab;
  readonly isStartup?: boolean;
  readonly reason?: ChooseProjectDialogReason;
};

export function ChooseProjectDialog(props: ChooseProjectDialogProps) {
  const dispatch = useAppDispatch();
  const baseDesignUrl = useAppSelector(getClientUrls).designUrl;

  const createProjectPanelRef = useRef<CreateProjectPanelMethods>(null);

  const runningLocally = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const designUrl = runningLocally ? `${window.origin}/` : baseDesignUrl;

  const userIsOnFreeTier = useAppSelector(hasFreeSubscription);

  const userIsRoot = useAppSelector(isLoggedInUserRoot);
  const userSignedUpThemselves = useAppSelector(isSelfServiceUser);
  const trialAvailable = useAppSelector(canStartTrial);

  const blocking = (props.isStartup ?? false) && !userIsRoot;

  const [explicitlySelectedTab, setExplicitlySelectedTab] = useState<ChooseProjectDialogTab | null>(
    props.initialTab ?? null,
  );

  const projectsQuery = usePromise(designService.projects.fetchSummaries);
  const samplesQuery = usePromise(designService.sharedComponentSets.fetchSamples);

  const userHasNoProjects = projectsQuery.isSuccess && projectsQuery.data.length === 0;

  const canCreateProjects = !userIsOnFreeTier || userHasNoProjects;

  // With nothing to pick from, start on the tab that can actually get the user somewhere.
  const mode = explicitlySelectedTab ?? (userHasNoProjects ? 'create-project' : 'pick-project');

  return (
    <Dialog
      variant="design"
      contentSx={{ display: 'flex', flexDirection: 'column', minWidth: '450px' }}
      insistent={blocking}
      onCancel={blocking ? undefined : 'close'}
      cancelText={mode === 'create-project' ? 'Cancel' : 'Close'}
      submitText={mode === 'create-project' ? 'Create' : undefined}
      actionsNode={
        <Stack direction="row" spacing={2} sx={{ marginLeft: 3, marginRight: 'auto', paddingLeft: 1 }}>
          <ResourceLink href={DOCUMENTATION_URL} icon={<MenuBookOutlinedIcon fontSize="inherit" />}>
            Documentation
          </ResourceLink>

          <ResourceLink href={TRAINING_URL} icon={<SchoolOutlinedIcon fontSize="inherit" />}>
            Training
          </ResourceLink>
        </Stack>
      }
      onSubmit={
        mode === 'create-project'
          ? async () => {
              if (createProjectPanelRef.current == null) return;

              const result = await createProjectPanelRef.current.submit();
              if (result.success === false) {
                return; // keep dialog open so it can display errors to user
              }

              dispatch(closeCurrentPopup());
            }
          : undefined
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {props.reason && (
          <InlineNotification type={REASON_MESSAGE_TYPES[props.reason]}>
            {REASON_MESSAGES[props.reason]}
          </InlineNotification>
        )}

        {projectsQuery.isError ? (
          <>
            <InlineNotification type="error">Could not load projects.</InlineNotification>

            <Button variant="outlined" sx={{ alignSelf: 'center' }} onClick={() => window.location.reload()}>
              Refresh
            </Button>
          </>
        ) : projectsQuery.isPending ? (
          <CircularProgress sx={{ alignSelf: 'center' }} />
        ) : (
          <>
            <Tabs
              indicatorColor="secondary"
              textColor="inherit"
              value={mode}
              onChange={(_, tab) => setExplicitlySelectedTab(tab)}
              variant="fullWidth"
            >
              <ChooseProjectDialogTab value="pick-project" label="Choose Project" />

              {canCreateProjects ? (
                <ChooseProjectDialogTab value="create-project" label="Create Project" />
              ) : (
                <OptionalTooltip
                  visible
                  title={trialAvailable ? 'Start your free trial to access' : 'Upgrade to access'}
                >
                  <ChooseProjectDialogTab disabled value="create-project" label="Create Project" />
                </OptionalTooltip>
              )}

              {userSignedUpThemselves && samplesQuery.isSuccess && (
                <ChooseProjectDialogTab value="open-sample" label="Open Sample" />
              )}
            </Tabs>

            {(() => {
              switch (mode) {
                case 'pick-project': {
                  return <PickProjectPanel designUrl={designUrl} projectSummaries={projectsQuery.data} />;
                }

                case 'create-project': {
                  return (
                    <CreateProjectPanel
                      ref={createProjectPanelRef}
                      designUrl={designUrl}
                      projectSummaries={projectsQuery.data}
                    />
                  );
                }

                case 'open-sample': {
                  if (!samplesQuery.isSuccess) return null;

                  return <OpenSamplePanel designUrl={designUrl} sampleSharedComponentSets={samplesQuery.data} />;
                }

                default:
                  assertExhaustiveSwitch(mode);
              }
            })()}
          </>
        )}
      </Box>
    </Dialog>
  );
}

function ResourceLink(props: PropsWithChildren<{ href: string; icon: ReactNode }>) {
  return (
    <Link
      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
      href={props.href}
      target="_blank"
      rel="noreferrer noopener"
      underline="hover"
      color="text.secondary"
      variant="body2"
    >
      {props.icon}
      {props.children}
    </Link>
  );
}
